<script setup>
import { onBeforeUnmount, onMounted } from 'vue'
import { RouterLink } from 'vue-router'

function calHeight() {
  const innerheight = window.innerHeight / 100
  document
    .querySelector(':root')
    .style.setProperty('--vh', `${innerheight.toString()}px`)
}

onMounted(() => {
  calHeight()
  window.addEventListener('resize', calHeight)
})

onBeforeUnmount(() => {
  window.addEventListener('resize', calHeight)
})

const cdnArchivos = process.env.VUE_APP_CDN_ARCHIVOS
</script>

<template>
  <div class="pagina-inicio">
    <div class="contenedor-portada">
      <video
        id="v-escritorio"
        class="portada"
        autoplay
        muted
        loop
        playsinline
      >
        <source
          :src="`${cdnArchivos}portada/portada_escritorio.webm`"
          type="video/webm"
        />
      </video>
      <video
        id="v-movil"
        class="portada"
        autoplay
        muted
        loop
        playsinline
      >
        <source
          :src="`${cdnArchivos}portada/portada_movil.webm`"
          type="video/webm"
        />
      </video>
      <div class="degradado-negro-abajo">
        <div class="contenido-abajo">
          <div class="contenedor ancho-fijo">
            <h1 class="titulo-eni texto-centrado texto-color-1">
              Ecosistemas Nacionales Informáticos (ENI)
            </h1>
          </div>
        </div>
      </div>
    </div>

    <div
      class="p-y-8"
      style="background-color: #99e0ff"
    >
      <div class="contenedor ancho-lectura">
        <p class="texto-tamanio-2-mov texto-tamanio-3-esc">
          Los ENI son espacios colaborativos, y de acceso abierto, que
          contribuyen al conocimiento local y regional para atender los
          problemas prioritarios de México, al almacenar, procesar, analizar y
          difundir información humanística, científica y tecnológica con la
          finalidad de maximizar la incidencia a favor del cuidado ambiental y
          de las personas más necesitadas.
        </p>
      </div>
    </div>

    <section class="contenedor ancho-fijo p-t-maximo-esc p-t-4-mov p-b-10">
      <h2 class="texto-centrado-esc">
        Estructura de los Ecosistemas Nacionales Informáticos
      </h2>
      <div class="flex flex-contenido-separado m-t-5 flex-align-items-center">
        <picture class="columna-8">
          <source
            :srcset="`${cdnArchivos}inicio/eni-1-esc.webp`"
            type="image/webp"
          />
          <img
            class="borde-redondeado-20"
            :src="`${cdnArchivos}inicio/eni-1-esc.png`"
            alt="Estructura ENI"
          />
        </picture>

        <div class="columna-8">
          <p>
            Los ENI están alineados a los
            <SisdaiEnlaceExterno
              texto="Programas Nacionales Estratégicos"
              enlace="https://conahcyt.mx/pronaces/"
            />
            y atienden problemáticas de relevancia nacional.
          </p>
          <p>
            Su contenido se aborda en capítulos y subcapítulos, en ellos puedes
            acceder a información en materia de humanidades, ciencias y
            tecnologías.
          </p>
        </div>
      </div>
    </section>

    <section style="background-color: #edf2f5">
      <div
        class="contenedor ancho-fijo p-t-8-esc p-t-4-mov p-b-8-esc p-b-4-mov"
      >
        <h2 class="texto-centrado-esc">¿Qué puedes hacer en los ENI?</h2>

        <div class="flex flex-contenido-separado m-y-5 flex-align-items-center">
          <div class="columna-8-esc">
            <h3 class="subtitulos-color-realce">
              Enterarte de investigaciones científicas de la mano de equipos de
              trabajo en México
            </h3>
            <picture class="columna-8">
              <source
                :srcset="`${cdnArchivos}inicio/eni-2-mov.webp`"
                type="image/webp"
              />
              <img
                class="borde-redondeado-20 oculta-escritorio"
                :src="`${cdnArchivos}inicio/eni-2-mov.png`"
                alt="Investigaciones científicas"
              />
            </picture>
            <p>
              La información disponible en los ENI y sus capítulos, es resultado
              del trabajo de personas investigadoras que generan conocimiento
              sobre las problemáticas y temas prioritarios, los cuales requieren
              atención en la agenda de desarrollo nacional.
            </p>
          </div>
          <picture class="columna-8">
            <source
              :srcset="`${cdnArchivos}inicio/eni-2-esc.webp`"
              type="image/webp"
            />
            <img
              class="columna-8-esc borde-redondeado-20 oculta-movil"
              :src="`${cdnArchivos}inicio/eni-2-esc.png`"
              alt="Investigaciones científicas"
            />
          </picture>
        </div>

        <div class="flex flex-contenido-separado m-y-5 flex-align-items-center">
          <picture class="columna-8">
            <source
              :srcset="`${cdnArchivos}inicio/eni-3-esc.webp`"
              type="image/webp"
            />
            <img
              class="columna-8-esc borde-redondeado-20 oculta-movil"
              :src="`${cdnArchivos}inicio/eni-3-esc.png`"
              alt="Visualización de información"
            />
          </picture>
          <div class="columna-8-esc">
            <h3 class="subtitulos-color-realce">
              Visualizar y comprender información compleja
            </h3>
            <picture class="columna-8">
              <source
                :srcset="`${cdnArchivos}inicio/eni-3-mov.webp`"
                type="image/webp"
              />
              <img
                class="borde-redondeado-20 oculta-escritorio"
                :src="`${cdnArchivos}inicio/eni-3-mov.png`"
                alt="Visualización de información"
              />
            </picture>
            <p>
              En los capítulos de ENI podrás encontrar estudios científicos,
              documentos con rigor académico, mapas y gráficos interactivos,
              narrativas gráficas y visualizaciones de datos que te permitirán
              acceder a información compleja sobre las problemáticas del país.
            </p>
          </div>
        </div>

        <div class="flex flex-contenido-separado m-y-5 flex-align-items-center">
          <div class="columna-8-esc">
            <h3 class="subtitulos-color-realce">
              Descargar bases de datos para hacer tus propios análisis
            </h3>
            <picture class="columna-8">
              <source
                :srcset="`${cdnArchivos}inicio/eni-4-mov.webp`"
                type="image/webp"
              />
              <img
                class="borde-redondeado-20 oculta-escritorio"
                :src="`${cdnArchivos}inicio/eni-4-mov.png`"
                alt="Descarga de bases de datos"
              />
            </picture>
            <p>
              ¿Te interesó alguna información específica? descárgala. Las bases
              de datos en los ENI son abiertas y están listas para su descarga.
            </p>
          </div>
          <picture class="columna-8">
            <source
              :srcset="`${cdnArchivos}inicio/eni-4-esc.webp`"
              type="image/webp"
            />
            <img
              class="columna-8-esc borde-redondeado-20 oculta-movil"
              :src="`${cdnArchivos}inicio/eni-4-esc.png`"
              alt="Descarga de bases de datos"
            />
          </picture>
        </div>
      </div>
    </section>

    <div class="contenedor ancho-fijo m-t-10-esc m-t-5-mov m-b-8">
      <h2 class="texto-centrado-esc">Catálogo</h2>
      <div class="contenedor ancho-lectura contenedor-centro m-b-4">
        <p class="texto-centrado-esc p-x-4-esc">
          En este apartado podrás explorar los Ecosistemas Nacionales
          Informáticos y tener acceso directo a sus capítulos y subcapítulos
        </p>
      </div>

      <RouterLink
        class="tarjeta-hipervinculo-externo tarjeta-alta-mobile"
        to="/catalogo"
      >
        <picture class="fondo-imagen">
          <source :srcset="`${cdnArchivos}inicio/img5.png`" />
          <img
            class="fondo-imagen"
            :src="`${cdnArchivos}inicio/img5.png`"
            alt=""
          />
        </picture>

        <div class="degradado-negro-abajo">
          <div class="contenido-abajo">
            <p class="titulo-pagina texto-color-1 txt-con-flecha">
              ENI, capítulos y subcapítulos
              <span class="icono-flecha-arriba-derecha m-y-0"></span>
            </p>
          </div>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<style lang="scss">
.pagina-inicio {
  .contenedor-portada {
    #v-escritorio {
      display: none;
      @media (min-width: map-get($breakpoints, 'esc')) {
        display: block;
      }
    }
    #v-movil {
      display: block;
      @media (min-width: map-get($breakpoints, 'esc')) {
        display: none;
      }
    }
  }

  .flex-align-items-center {
    align-items: center;
  }

  h2,
  h3,
  h4 {
    &.subtitulos-color-realce {
      color: #003652;
    }
  }

  @include mediaquery('esc') {
    .texto-centrado-esc {
      text-align: center;
    }
    .oculta-escritorio {
      display: none;
    }
  }

  @include mediaquery('mov') {
    .oculta-movil {
      display: none;
    }
  }

  span.icono-flecha-arriba-derecha {
    float: right;
    margin-left: auto;
  }
  p.txt-con-flecha {
    display: flex;
    align-items: flex-end;
  }
  .tarjeta-alta-mobile {
    height: 400px;

    @media (min-width: map-get($breakpoints, 'esc')) {
      height: auto;
    }
  }
}
</style>
